import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import localization from 'assets/localization/localization';
import DropDownInput from '../Input/dropDownInput/dropDownInput';
import ConstantInput from '../Input/constantInput/constantInput';
import { DatePickerInput } from 'rc-datepicker';
import { FormGroup } from 'react-bootstrap';
import Images from 'assets/images/imageData';

export const SITE_DASHBOARD_INITIAL_FILTER = {
  client_name: '',
  site_no: '',
  infra_project_id: '',
  site_status: '',
  contract_period: '',
  onboarding_from_date: '',
  onboarding_to_date: '',
  live_from_date: '',
  live_to_date: '',
  from_date: '',
  to_date: '',
};

const formatDateDDMMYYYY = (date) => {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
};

const getDefaultCreatedRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return {
    from_date: formatDateDDMMYYYY(start),
    to_date: formatDateDDMMYYYY(end),
  };
};

const DEFAULT_SITE_STATUS_OPTIONS = [
  { id: 'Active', name: 'Active' },
  { id: 'Inactive', name: 'Inactive' },
  { id: 'Approved', name: 'Approved' },
  { id: 'Live', name: 'Live' },
  { id: 'Hold', name: 'Hold' },
  { id: 'In Progress', name: 'In Progress' },
];

const normalizeOptions = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (item == null) return null;
      if (typeof item === 'string' || typeof item === 'number') {
        const v = String(item);
        return { id: v, name: v };
      }
      const id = item.id ?? item.value ?? item.code ?? item.name;
      const name = item.name ?? item.label ?? item.value ?? item.title ?? item.id;
      if (id == null && name == null) return null;
      return { ...item, id, name };
    })
    .filter(Boolean);
};

const SiteDashboardFilter = ({ applyFilter, listFilterData, appliedFilterValues }) => {
  const [filterFieldsData, setFilterFieldsData] = useState(() => ({
    ...SITE_DASHBOARD_INITIAL_FILTER,
    ...getDefaultCreatedRange(),
  }));
  const [isFocusOnboardingFrom, setIsFocusOnboardingFrom] = useState(false);
  const [isFocusOnboardingTo, setIsFocusOnboardingTo] = useState(false);
  const [isFocusLiveFrom, setIsFocusLiveFrom] = useState(false);
  const [isFocusLiveTo, setIsFocusLiveTo] = useState(false);
  const [isFocusFrom, setIsFocusFrom] = useState(false);
  const [isFocusTo, setIsFocusTo] = useState(false);
  const [siteStatusLabel, setSiteStatusLabel] = useState('');

  const siteStatusOptions = useMemo(() => {
    const fromApi =
      listFilterData?.site_status ??
      listFilterData?.siteStatus ??
      listFilterData?.ops_status ??
      listFilterData?.opsStatus ??
      listFilterData?.site_status_list ??
      listFilterData?.siteStatusList;
    const normalized = normalizeOptions(fromApi);
    return normalized.length ? normalized : DEFAULT_SITE_STATUS_OPTIONS;
  }, [listFilterData]);

  useEffect(() => {
    if (appliedFilterValues) {
      setFilterFieldsData({
        client_name: appliedFilterValues.client_name ?? '',
        site_no: appliedFilterValues.site_no ?? '',
        infra_project_id: appliedFilterValues.infra_project_id ?? '',
        site_status: appliedFilterValues.site_status ?? appliedFilterValues.ops_status ?? '',
        contract_period: appliedFilterValues.contract_period ?? '',
        onboarding_from_date: appliedFilterValues.onboarding_from_date ?? '',
        onboarding_to_date: appliedFilterValues.onboarding_to_date ?? '',
        live_from_date: appliedFilterValues.live_from_date ?? '',
        live_to_date: appliedFilterValues.live_to_date ?? '',
        from_date: appliedFilterValues.from_date ?? '',
        to_date: appliedFilterValues.to_date ?? '',
      });
      setIsFocusOnboardingFrom(Boolean(appliedFilterValues.onboarding_from_date));
      setIsFocusOnboardingTo(Boolean(appliedFilterValues.onboarding_to_date));
      setIsFocusLiveFrom(Boolean(appliedFilterValues.live_from_date));
      setIsFocusLiveTo(Boolean(appliedFilterValues.live_to_date));
      setIsFocusFrom(Boolean(appliedFilterValues.from_date));
      setIsFocusTo(Boolean(appliedFilterValues.to_date));
      const appliedStatus =
        appliedFilterValues.site_status ?? appliedFilterValues.ops_status ?? '';
      if (appliedStatus && siteStatusOptions.length) {
        const statusMatch = siteStatusOptions.find(
          (s) => String(s.id) === String(appliedStatus)
        );
        setSiteStatusLabel(statusMatch?.name ?? '');
      }
    } else {
      setFilterFieldsData({
        ...SITE_DASHBOARD_INITIAL_FILTER,
        ...getDefaultCreatedRange(),
      });
      setIsFocusOnboardingFrom(false);
      setIsFocusOnboardingTo(false);
      setIsFocusLiveFrom(false);
      setIsFocusLiveTo(false);
      setIsFocusFrom(false);
      setIsFocusTo(false);
      setSiteStatusLabel('');
    }
  }, [appliedFilterValues, siteStatusOptions]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const sanitized = {
          ...filterFieldsData,
          onboarding_from_date:
            String(filterFieldsData.onboarding_from_date || '').toLowerCase() === 'invalid date'
              ? ''
              : filterFieldsData.onboarding_from_date,
          onboarding_to_date:
            String(filterFieldsData.onboarding_to_date || '').toLowerCase() === 'invalid date'
              ? ''
              : filterFieldsData.onboarding_to_date,
          live_from_date:
            String(filterFieldsData.live_from_date || '').toLowerCase() === 'invalid date'
              ? ''
              : filterFieldsData.live_from_date,
          live_to_date:
            String(filterFieldsData.live_to_date || '').toLowerCase() === 'invalid date'
              ? ''
              : filterFieldsData.live_to_date,
          from_date:
            String(filterFieldsData.from_date || '').toLowerCase() === 'invalid date'
              ? ''
              : filterFieldsData.from_date,
          to_date:
            String(filterFieldsData.to_date || '').toLowerCase() === 'invalid date'
              ? ''
              : filterFieldsData.to_date,
        };
        applyFilter(sanitized);
      }}
    >
      <ConstantInput
        label={localization.clientBuildingOrPartnerName}
        type="text"
        value={filterFieldsData.client_name}
        handleChange={(val) =>
          setFilterFieldsData((prev) => ({ ...prev, client_name: val ?? '' }))
        }
      />

      <ConstantInput
        label={localization.siteNumber}
        type="text"
        value={filterFieldsData.site_no}
        handleChange={(val) =>
          setFilterFieldsData((prev) => ({ ...prev, site_no: val ?? '' }))
        }
      />

      <ConstantInput
        label={localization.projectId}
        type="text"
        value={filterFieldsData.infra_project_id}
        handleChange={(val) =>
          setFilterFieldsData((prev) => ({ ...prev, infra_project_id: val ?? '' }))
        }
      />

      <DropDownInput
        label="Site Status"
        data={siteStatusOptions}
        value={siteStatusLabel}
        getIdValue={(val) => {
          setFilterFieldsData((prev) => ({ ...prev, site_status: val?.id ?? '' }));
          setSiteStatusLabel(val?.value ?? '');
        }}
      />

      <ConstantInput
        label="Contract Period (months)"
        type="number"
        value={filterFieldsData.contract_period}
        handleChange={(val) =>
          setFilterFieldsData((prev) => ({
            ...prev,
            contract_period: val == null ? '' : String(val),
          }))
        }
      />

      <fieldset>
        <h4>Onboarding Between</h4>
        <div className="d-flex justify-content-end mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setFilterFieldsData((prev) => ({
                ...prev,
                onboarding_from_date: '',
                onboarding_to_date: '',
              }));
              setIsFocusOnboardingFrom(false);
              setIsFocusOnboardingTo(false);
            }}
          >
            Clear dates
          </button>
        </div>

        <FormGroup
          className={`date custom_date_picker ${isFocusOnboardingFrom ? 'active-date' : ''}`}
        >
          <DatePickerInput
            onChange={(_jsdate, dateString) =>
              setFilterFieldsData((prev) => ({ ...prev, onboarding_from_date: dateString }))
            }
            value={filterFieldsData.onboarding_from_date}
            autoClose
            onShow={() => setIsFocusOnboardingFrom(true)}
            onHide={() =>
              setIsFocusOnboardingFrom(Boolean(filterFieldsData.onboarding_from_date))
            }
            closeOnClickOutside
            showOnInputClick
            locale="en"
            className="my-custom-datepicker-component"
            displayFormat="DD-MM-YYYY"
            returnFormat="DD-MM-YYYY"
            maxDate={new Date()}
          />
          <label>{localization.fromDate}</label>
        </FormGroup>

        <FormGroup
          className={`date custom_date_picker ${isFocusOnboardingTo ? 'active-date' : ''}`}
        >
          <DatePickerInput
            onChange={(_jsdate, dateString) =>
              setFilterFieldsData((prev) => ({ ...prev, onboarding_to_date: dateString }))
            }
            value={filterFieldsData.onboarding_to_date}
            autoClose
            onShow={() => setIsFocusOnboardingTo(true)}
            onHide={() => setIsFocusOnboardingTo(Boolean(filterFieldsData.onboarding_to_date))}
            closeOnClickOutside
            showOnInputClick
            locale="en"
            className="my-custom-datepicker-component"
            displayFormat="DD-MM-YYYY"
            returnFormat="DD-MM-YYYY"
            maxDate={new Date()}
          />
          <label>{localization.toDate}</label>
        </FormGroup>
      </fieldset>

      <fieldset>
        <h4>Live Between</h4>
        <div className="d-flex justify-content-end mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setFilterFieldsData((prev) => ({ ...prev, live_from_date: '', live_to_date: '' }));
              setIsFocusLiveFrom(false);
              setIsFocusLiveTo(false);
            }}
          >
            Clear dates
          </button>
        </div>

        <FormGroup
          className={`date custom_date_picker ${isFocusLiveFrom ? 'active-date' : ''}`}
        >
          <DatePickerInput
            onChange={(_jsdate, dateString) =>
              setFilterFieldsData((prev) => ({ ...prev, live_from_date: dateString }))
            }
            value={filterFieldsData.live_from_date}
            autoClose
            onShow={() => setIsFocusLiveFrom(true)}
            onHide={() => setIsFocusLiveFrom(Boolean(filterFieldsData.live_from_date))}
            closeOnClickOutside
            showOnInputClick
            locale="en"
            className="my-custom-datepicker-component"
            displayFormat="DD-MM-YYYY"
            returnFormat="DD-MM-YYYY"
            maxDate={new Date()}
          />
          <label>{localization.fromDate}</label>
        </FormGroup>

        <FormGroup className={`date custom_date_picker ${isFocusLiveTo ? 'active-date' : ''}`}>
          <DatePickerInput
            onChange={(_jsdate, dateString) =>
              setFilterFieldsData((prev) => ({ ...prev, live_to_date: dateString }))
            }
            value={filterFieldsData.live_to_date}
            autoClose
            onShow={() => setIsFocusLiveTo(true)}
            onHide={() => setIsFocusLiveTo(Boolean(filterFieldsData.live_to_date))}
            closeOnClickOutside
            showOnInputClick
            locale="en"
            className="my-custom-datepicker-component"
            displayFormat="DD-MM-YYYY"
            returnFormat="DD-MM-YYYY"
            maxDate={new Date()}
          />
          <label>{localization.toDate}</label>
        </FormGroup>
      </fieldset>

      <fieldset>
        <h4>{localization.createdBetween}</h4>
        <div className="d-flex justify-content-end mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setFilterFieldsData((prev) => ({ ...prev, from_date: '', to_date: '' }));
              setIsFocusFrom(false);
              setIsFocusTo(false);
            }}
          >
            Clear dates
          </button>
        </div>
        <FormGroup
          className={`date custom_date_picker ${isFocusFrom ? 'active-date' : ''}`}
        >
          <DatePickerInput
            onChange={(_jsdate, dateString) =>
              setFilterFieldsData((prev) => ({ ...prev, from_date: dateString }))
            }
            value={filterFieldsData.from_date}
            autoClose
            onShow={() => setIsFocusFrom(true)}
            onHide={() => setIsFocusFrom(Boolean(filterFieldsData.from_date))}
            closeOnClickOutside
            showOnInputClick
            locale="en"
            className="my-custom-datepicker-component"
            displayFormat="DD-MM-YYYY"
            returnFormat="DD-MM-YYYY"
            maxDate={new Date()}
          />
          <label>{localization.fromDate}</label>
        </FormGroup>

        <FormGroup
          className={`date custom_date_picker ${isFocusTo ? 'active-date' : ''}`}
        >
          <DatePickerInput
            onChange={(_jsdate, dateString) =>
              setFilterFieldsData((prev) => ({ ...prev, to_date: dateString }))
            }
            value={filterFieldsData.to_date}
            autoClose
            onShow={() => setIsFocusTo(true)}
            onHide={() => setIsFocusTo(Boolean(filterFieldsData.to_date))}
            closeOnClickOutside
            showOnInputClick
            locale="en"
            className="my-custom-datepicker-component"
            displayFormat="DD-MM-YYYY"
            returnFormat="DD-MM-YYYY"
            maxDate={new Date()}
          />
          <label>{localization.toDate}</label>
        </FormGroup>
      </fieldset>

      <br />
      <br />
      <br />
      <div className="btn-wrapper">
        <button type="submit" className="btn btn-primary">
          {localization.applyFilter}
          <img src={Images?.ArrowRight} alt="" />
        </button>
        <br />
        <br />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            const next = {
              ...SITE_DASHBOARD_INITIAL_FILTER,
              ...getDefaultCreatedRange(),
            };
            setFilterFieldsData(next);
            setSiteStatusLabel('');
            applyFilter(next);
          }}
        >
          {localization.removeFilter}
        </button>
      </div>
    </form>
  );
};

SiteDashboardFilter.propTypes = {
  applyFilter: PropTypes.func.isRequired,
  listFilterData: PropTypes.shape({
    // (kept generic) other filters may be passed through listFilterData
  }),
  appliedFilterValues: PropTypes.object,
};

SiteDashboardFilter.defaultProps = {
  listFilterData: null,
  appliedFilterValues: null,
};

export default SiteDashboardFilter;
